"use client";

import React from "react";
import { Highlight, themes, Prism } from "prism-react-renderer";

// Ensure global Prism is available for prismjs language extensions
if (typeof globalThis !== "undefined" && !(globalThis as unknown as { Prism: unknown }).Prism) {
  (globalThis as unknown as { Prism: typeof Prism }).Prism = Prism;
}

// Dynamically inject bash and diff grammars if needed
try {
  if (!Prism.languages.bash) {
    require("prismjs/components/prism-bash");
  }
} catch {
  // ignore in non-node environments
}

try {
  if (!Prism.languages.diff) {
    require("prismjs/components/prism-diff");
  }
} catch {
  // ignore
}

export function normalizeLanguage(lang?: string): string {
  if (!lang) return "typescript";
  const clean = lang.toLowerCase().trim().replace(/^\./, "");
  switch (clean) {
    case "ts":
      return "typescript";
    case "tsx":
      return "tsx";
    case "js":
    case "mjs":
    case "cjs":
      return "javascript";
    case "jsx":
      return "jsx";
    case "html":
    case "xml":
    case "svg":
      return "markup";
    case "sh":
    case "bash":
    case "zsh":
    case "shell":
    case "terminal":
    case "console":
      return "bash";
    case "yml":
    case "yaml":
      return "yaml";
    case "py":
    case "python":
      return "python";
    case "md":
    case "mdx":
      return "markdown";
    case "json":
      return "json";
    case "css":
      return "css";
    case "sql":
      return "sql";
    case "diff":
      return "diff";
    case "go":
      return "go";
    case "rust":
      return "rust";
    default:
      // If language exists in Prism, use it; otherwise fallback to plaintext or typescript
      return Prism.languages[clean] ? clean : "typescript";
  }
}

interface CodeHighlightProps {
  code: string;
  language?: string;
  showLineNumbers?: boolean;
  className?: string;
}

export function CodeHighlight({
  code,
  language,
  showLineNumbers,
  className = "",
}: CodeHighlightProps) {
  const cleanCode = (code || "").trimEnd();
  const resolvedLang = normalizeLanguage(language);

  return (
    <Highlight
      theme={themes.oneDark}
      code={cleanCode}
      language={resolvedLang}
    >
      {({ className: highlightClass, style, tokens, getLineProps, getTokenProps }) => {
        const isMultiLine = tokens.length > 2;
        const displayLineNumbers =
          showLineNumbers !== undefined
            ? showLineNumbers
            : isMultiLine && resolvedLang !== "bash";

        return (
          <pre
            className={`overflow-x-auto p-4 sm:p-5 font-mono text-xs sm:text-sm leading-relaxed selection:bg-zinc-800 ${highlightClass} ${className}`}
            style={{ ...style, backgroundColor: "transparent" }}
          >
            <code className="table w-full border-collapse">
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line })} className="table-row">
                  {displayLineNumbers && (
                    <span className="table-cell select-none pr-4 text-right font-mono text-[11px] sm:text-xs text-zinc-600/80 w-8 min-w-[2.25rem] tabular-nums">
                      {i + 1}
                    </span>
                  )}
                  <span className="table-cell whitespace-pre">
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token })} />
                    ))}
                  </span>
                </div>
              ))}
            </code>
          </pre>
        );
      }}
    </Highlight>
  );
}
